import { strokeShapeOutline, shapeGeometry, isDoorShape, doorGeometry, parseInches, formatInches } from "./windowShapes";
/**
 * measureAnnotate — draws measurement annotations on a window/door photo.
 *
 * Output (JPEG dataURL):
 *  - dashed rectangle around the detected opening
 *  - horizontal double-arrow along the BASE with the measured WIDTH
 *  - vertical double-arrow along the SIDE with the measured HEIGHT
 *  - header chip with location + expected size (PDF) + match/mismatch flag
 *
 * Colors: red = differs from the PDF, green = matches, amber = no PDF reference.
 */

const COLORS = {
  bad: "#dc2626",
  good: "#059669",
  neutral: "#d97706",
  chipBg: "rgba(15,23,42,0.86)",
  chipText: "#ffffff",
  labelBg: "rgba(255,255,255,0.95)",
};

/** Trim a written note for the photo chip (keeps it readable). */
function clipNote(text) {
  const n = (text || "").trim().replace(/\s+/g, " ");
  return n.length > 64 ? `${n.slice(0, 61)}…` : n;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Double-headed arrow with end ticks, drawn with a white halo for visibility. */
function drawArrow(ctx, x1, y1, x2, y2, color, lw) {
  const head = lw * 3.2;
  const ang = Math.atan2(y2 - y1, x2 - x1);
  const passes = [
    { stroke: "rgba(255,255,255,0.9)", width: lw * 2.4 },
    { stroke: color, width: lw },
  ];
  for (const p of passes) {
    ctx.strokeStyle = p.stroke;
    ctx.lineWidth = p.width;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
    // arrowheads
    for (const [px, py, dir] of [[x1, y1, ang], [x2, y2, ang + Math.PI]]) {
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(px + head * Math.cos(dir + 0.42), py + head * Math.sin(dir + 0.42));
      ctx.moveTo(px, py);
      ctx.lineTo(px + head * Math.cos(dir - 0.42), py + head * Math.sin(dir - 0.42));
      ctx.stroke();
    }
    // end ticks (perpendicular)
    const tx = Math.cos(ang + Math.PI / 2) * head;
    const ty = Math.sin(ang + Math.PI / 2) * head;
    for (const [px, py] of [[x1, y1], [x2, y2]]) {
      ctx.beginPath();
      ctx.moveTo(px - tx, py - ty);
      ctx.lineTo(px + tx, py + ty);
      ctx.stroke();
    }
  }
}

/** Pill label with colored bold text. Returns its rect. */
function drawLabel(ctx, text, cx, cy, color, fontPx) {
  ctx.font = `800 ${fontPx}px system-ui, -apple-system, sans-serif`;
  const padX = fontPx * 0.6;
  const padY = fontPx * 0.38;
  const w = ctx.measureText(text).width + padX * 2;
  const h = fontPx + padY * 2;
  const x = cx - w / 2;
  const y = cy - h / 2;
  ctx.fillStyle = COLORS.labelBg;
  roundRect(ctx, x, y, w, h, h / 2);
  ctx.fill();
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(2, fontPx * 0.1);
  roundRect(ctx, x, y, w, h, h / 2);
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, cx, cy + fontPx * 0.05);
  return { x, y, w, h };
}

/**
 * @param {Object} opts
 * @param {string} opts.dataUrl        source photo (dataURL)
 * @param {Object|null} opts.box       normalized {x,y,w,h} of the opening (AI detection)
 * @param {string} opts.widthText      measured width, e.g. `35 1/2"`
 * @param {string} opts.heightText     measured height, e.g. `79 3/4"`
 * @param {string} opts.locationText   e.g. `Master Bathroom · #4816041`
 * @param {string} opts.expectedText   e.g. `PDF: 35 1/2" x 79 3/4"` ("" when no PDF)
 * @param {boolean|null} opts.match    true = matches PDF, false = wrong, null = no reference
 * @returns {Promise<string>} annotated JPEG dataURL
 */
// Leg / spring line end for each shape comes from shapeGeometry() (real leg ÷ height ratio).

/**
 * One arrow per measurement, placed where that measurement physically is:
 * width along the base · total height on the SIDE for square windows (rectangle) and in the
 * MIDDLE from base to the tallest point for curved/peaked shapes (tall side for quarter-circle /
 * right-triangle) · leg height from the base to where the curve starts · trapezoid legs at
 * each side + top width · circle diameter.
 */
function shapeArrows({ shape, side, bx, by, bw, bh, dims, widthText, heightText, s }) {
  const d = dims || {};
  const inset = 14 * s;
  const A = [];
  const w = d.w || widthText, h = d.h || heightText;
  if (isDoorShape(shape)) {
    // door unit: w/h = whole unit. Parts (sidelights, transoms) only get arrows when measured;
    // the door slab arrow is derived (total − sidelights) and the height arrow is the slab (total − transom).
    const { L, R, T, TL, TR, split } = doorGeometry(shape, { ...d, side: side || d.side });
    const right = (side || d.side || "L") === "R";
    const xL = bx + bw * L, xR = bx + bw - bw * R, yT = by + bh * T;
    const baseY = by + bh - inset;
    const W = parseInches(d.w), H = parseInches(d.h);
    const slL = L ? parseInches(d.slL || d.sl) : null, slR = R ? parseInches(d.slR || d.sl) : null;
    const anySL = slL != null || slR != null;
    if (slL != null) A.push({ x1: bx, y1: baseY, x2: xL, y2: baseY, text: d.slL || d.sl, label: "above" });
    if (slR != null) A.push({ x1: xR, y1: baseY, x2: bx + bw, y2: baseY, text: d.slR || d.sl, label: "above" });
    if (W != null && anySL && (L ? slL != null : true) && (R ? slR != null : true)) {
      A.push({ x1: xL, y1: baseY, x2: xR, y2: baseY, text: formatInches(W - (slL || 0) - (slR || 0)), label: "above" });
    } else if (d.w) {
      A.push({ x1: bx, y1: baseY, x2: bx + bw, y2: baseY, text: d.w, label: "above", dx: anySL ? 0 : -bw * 0.24 });
    }
    const tr = parseInches(d.tr);
    if (H != null && tr != null && T) A.push({ x1: xR - inset, y1: yT, x2: xR - inset, y2: by + bh, text: formatInches(H - tr), label: "left" });
    else if (d.h) A.push({ x1: bx + bw - inset, y1: by, x2: bx + bw - inset, y2: by + bh, text: d.h, label: "left" });
    if (split) {
      if (T && d.tr) A.push({ x1: (xL + xR) / 2, y1: by, x2: (xL + xR) / 2, y2: yT, text: d.tr, label: "right" });
      const tlText = L ? (d.trL || (!right ? d.trS : "")) : "", trText = R ? (d.trR || (right ? d.trS : "")) : "";
      if (tlText) A.push({ x1: (bx + xL) / 2, y1: by, x2: (bx + xL) / 2, y2: by + bh * TL, text: tlText, label: "right" });
      if (trText) A.push({ x1: (xR + bx + bw) / 2, y1: by, x2: (xR + bx + bw) / 2, y2: by + bh * TR, text: trText, label: "left" });
    } else if (T && d.tr) {
      A.push({ x1: bx + inset, y1: by, x2: bx + inset, y2: yT, text: d.tr, label: "right" });
    }
    return A;
  }
  const geo = shapeGeometry(shape, d);
  if (shape === "circle") {
    const cy = by + bh / 2;
    A.push({ x1: bx + inset, y1: cy, x2: bx + bw - inset, y2: cy, text: d.d || w, label: "below" });
    return A;
  }
  // width label sits on the LEFT quarter so it never collides with the centred height arrow
  if (w) A.push({ x1: bx, y1: by + bh - inset, x2: bx + bw, y2: by + bh - inset, text: w, label: "above", dx: shape === "trapezoid" ? 0 : -bw * 0.24 });
  if (shape === "trapezoid") {
    const yl = by + bh * (1 - geo.legL), yr = by + bh * (1 - geo.legR);
    if (d.legL) A.push({ x1: bx + inset, y1: yl, x2: bx + inset, y2: by + bh, text: d.legL, label: "right" });
    if (d.legR) A.push({ x1: bx + bw - inset, y1: yr, x2: bx + bw - inset, y2: by + bh, text: d.legR, label: "left" });
    if (d.top) A.push({ x1: bx, y1: Math.min(yl, yr) + inset, x2: bx + bw, y2: Math.min(yl, yr) + inset, text: d.top, label: "below" });
    return A;
  }
  const tallSide = (shape === "quarter_circle" || shape === "right_triangle");
  const square = !shape || shape === "rectangle";
  if (h) {
    let hx = bx + bw / 2, label = "right";
    if (square) { hx = bx + bw - inset; label = "left"; } // no curve → height on the right side
    else if (tallSide) { const r = side === "R" || side === "right"; hx = r ? bx + bw - inset : bx + inset; label = r ? "left" : "right"; }
    A.push({ x1: hx, y1: by, x2: hx, y2: by + bh, text: h, label });
  }
  if (geo.leg != null && d.leg) {
    const ly = by + bh * (1 - geo.leg);
    A.push({ x1: bx + inset, y1: ly, x2: bx + inset, y2: by + bh, text: d.leg, label: "right" });
  }
  return A;
}

// Lines first, labels LAST (queued with their transform) so no arrow ever crosses a number.
function drawShapeArrows(ctx, arrows, color, lw, font, W, H, labelQueue) {
  for (const a of arrows) drawArrow(ctx, a.x1, a.y1, a.x2, a.y2, color, lw);
  const t = ctx.getTransform();
  for (const a of arrows) {
    if (!a.text) continue;
    const mx = (a.x1 + a.x2) / 2, my = (a.y1 + a.y2) / 2;
    let lx = mx + (a.dx || 0), ly = my;
    if (a.label === "above") ly = my - font * 1.05;
    else if (a.label === "below") ly = my + font * 1.05;
    else if (a.label === "right") lx = mx + font * 1.9;
    else if (a.label === "left") lx = mx - font * 1.9;
    lx = Math.max(font * 1.6, Math.min(W - font * 1.6, lx));
    ly = Math.max(font * 0.8, Math.min(H - font * 0.8, ly));
    labelQueue.push({ text: a.text, lx, ly, color, font, t });
  }
}

function flushLabels(ctx, labelQueue) {
  ctx.save();
  for (const l of labelQueue) {
    ctx.setTransform(l.t);
    drawLabel(ctx, l.text, l.lx, l.ly, l.color, l.font);
  }
  ctx.restore();
  labelQueue.length = 0;
}

export function annotateMeasurePhoto({ dataUrl, box, widthText, heightText, locationText, expectedText, noteText, match, oldLabel, shape, side, extraText, dims }) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const W = img.naturalWidth;
        const H = img.naturalHeight;
        const canvas = document.createElement("canvas");
        canvas.width = W;
        canvas.height = H;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        const b = box && box.w > 0 && box.h > 0 ? box : { x: 0.14, y: 0.1, w: 0.72, h: 0.72 };
        const bx = b.x * W, by = b.y * H, bw = b.w * W, bh = b.h * H;
        const s = Math.max(W, H) / 1000; // scale factor
        const lw = Math.max(3, 4 * s);
        const font = Math.max(16, 26 * s);
        const color = match === false ? COLORS.bad : match === true ? COLORS.good : COLORS.neutral;

        // rotate everything around the box center for angled photos
        const ang = ((b.angle || 0) * Math.PI) / 180;
        ctx.save();
        if (ang) {
          ctx.translate(bx + bw / 2, by + bh / 2);
          ctx.rotate(ang);
          ctx.translate(-(bx + bw / 2), -(by + bh / 2));
        }

        // specialty shape outline (half circle, springline, …) inside the box
        if (shape && shape !== "rectangle") {
          ctx.save();
          ctx.strokeStyle = "rgba(255,255,255,0.95)"; ctx.lineWidth = lw * 1.6;
          strokeShapeOutline(ctx, shape, bx, by, bw, bh, side, dims);
          ctx.strokeStyle = color; ctx.lineWidth = lw * 0.8;
          strokeShapeOutline(ctx, shape, bx, by, bw, bh, side, dims);
          ctx.restore();
        }

        // one arrow per measurement, where it physically is (no dashed box once measured)
        const labelQueue = [];
        drawShapeArrows(ctx, shapeArrows({ shape, side, bx, by, bw, bh, dims, widthText, heightText, s }), color, lw, font, W, H, labelQueue);

        ctx.restore(); // end rotation
        flushLabels(ctx, labelQueue);

        // header strip ABOVE the photo: location + expected + verdict + note
        const line2 = expectedText
          ? `${match === false ? "✗" : match === true ? "✓" : ""} ${expectedText}`.trim()
          : "";
        const line3 = clipNote(noteText);
        const rows = [];
        if (locationText) rows.push({ text: locationText, title: true });
        if (match === false && oldLabel) rows.push({ text: `⚠ ${oldLabel}:`, color: "#fca5a5" });
        if (line2) rows.push({
          text: line2,
          color: match === false ? "#fca5a5" : match === true ? "#6ee7b7" : "#fcd34d",
        });
        if (line3) rows.push({ text: `📝 ${line3}`, color: "#e2e8f0" });
        const out = composeWithHeader(canvas, rows, s, font);

        resolve(out.toDataURL("image/jpeg", 0.9));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

/** Loose comparison of two size strings: `35 1/2"` ~ `35-1/2` ~ `35 1/2 in`. */
export function sizeEquals(a, b) {
  const norm = (v) => (v || "")
    .toLowerCase()
    .replace(/["”″]|in(ch(es)?)?\b/g, "")
    .replace(/[-–—]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return norm(a) !== "" && norm(a) === norm(b);
}

/**
 * Cropped, zoomed preview of ONE unit inside the opening — so the user knows
 * exactly WHICH window the current measurement refers to.
 * Crops around the (possibly rotated) box with breathing room and highlights
 * the unit with a dashed outline.
 *
 * @returns {Promise<string>} JPEG dataURL of the cropped preview
 */
export function cropUnitPreview({ dataUrl, box, padRatio = 0.4, maxSize = 900 }) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const W = img.naturalWidth;
        const H = img.naturalHeight;
        const b = box && box.w > 0 && box.h > 0 ? box : { x: 0.1, y: 0.1, w: 0.8, h: 0.8 };
        const u = unionBoxPx([b], W, H);
        const bw = u.x2 - u.x1;
        const bh = u.y2 - u.y1;
        const cx1 = Math.max(0, u.x1 - bw * padRatio);
        const cy1 = Math.max(0, u.y1 - bh * padRatio);
        const cx2 = Math.min(W, u.x2 + bw * padRatio);
        const cy2 = Math.min(H, u.y2 + bh * padRatio);
        const cw = cx2 - cx1;
        const ch = cy2 - cy1;
        const scale = Math.min(1.5, maxSize / Math.max(cw, ch));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(cw * scale));
        canvas.height = Math.max(1, Math.round(ch * scale));
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, cx1, cy1, cw, ch, 0, 0, canvas.width, canvas.height);

        // dashed highlight of the unit inside the crop (follows rotation)
        const s = Math.max(canvas.width, canvas.height) / 500;
        const bx = (b.x * W - cx1) * scale;
        const by = (b.y * H - cy1) * scale;
        const bwid = b.w * W * scale;
        const bhei = b.h * H * scale;
        const ang = ((b.angle || 0) * Math.PI) / 180;
        ctx.save();
        if (ang) {
          ctx.translate(bx + bwid / 2, by + bhei / 2);
          ctx.rotate(ang);
          ctx.translate(-(bx + bwid / 2), -(by + bhei / 2));
        }
        ctx.setLineDash([9 * s, 7 * s]);
        ctx.strokeStyle = "rgba(255,255,255,0.95)";
        ctx.lineWidth = 5 * s;
        ctx.strokeRect(bx, by, bwid, bhei);
        ctx.strokeStyle = "#f59e0b";
        ctx.lineWidth = 2.6 * s;
        ctx.strokeRect(bx, by, bwid, bhei);
        ctx.restore();
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

/** Pixel-space union of normalized boxes, accounting for each box's rotation. */
function unionBoxPx(boxes, W, H) {
  let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
  for (const b of (boxes || []).filter(Boolean)) {
    const cx = (b.x + b.w / 2) * W;
    const cy = (b.y + b.h / 2) * H;
    const hw = (b.w * W) / 2;
    const hh = (b.h * H) / 2;
    const a = ((b.angle || 0) * Math.PI) / 180;
    const cos = Math.cos(a), sin = Math.sin(a);
    for (const [px, py] of [[-hw, -hh], [hw, -hh], [-hw, hh], [hw, hh]]) {
      const rx = cx + px * cos - py * sin;
      const ry = cy + px * sin + py * cos;
      x1 = Math.min(x1, rx); y1 = Math.min(y1, ry);
      x2 = Math.max(x2, rx); y2 = Math.max(y2, ry);
    }
  }
  if (!isFinite(x1)) return { x1: W * 0.1, y1: H * 0.1, x2: W * 0.9, y2: H * 0.9 };
  return { x1, y1, x2, y2 };
}

/** Dark multi-line chip in the top-left corner. lines: [{text, color?}] */
/**
 * Draws the header rows in a SOLID strip ADDED ABOVE the photo — the text can
 * never sit on top of the measurement lines drawn on the picture itself.
 */
function composeWithHeader(srcCanvas, rows0, s, font) {
  const rows = (rows0 || []).filter((l) => l && l.text);
  if (!rows.length) return srcCanvas;
  const W = srcCanvas.width;
  const mctx = srcCanvas.getContext("2d");
  const fh = (l) => (l.title ? font * 1.05 : font * 0.82);
  const pad = font * 0.7;
  let maxW = 0;
  for (const l of rows) {
    mctx.font = `${l.title ? 800 : 700} ${fh(l)}px system-ui, -apple-system, sans-serif`;
    maxW = Math.max(maxW, mctx.measureText(l.text).width);
  }
  const fit = Math.min(1, (W - pad * 2) / Math.max(1, maxW)); // shrink long lines to fit
  const stripH = Math.round(rows.reduce((acc, l) => acc + fh(l) * fit * 1.4, 0) + pad * 1.2);
  const out = document.createElement("canvas");
  out.width = W;
  out.height = srcCanvas.height + stripH;
  const ctx = out.getContext("2d");
  ctx.fillStyle = "#0f172a";
  ctx.fillRect(0, 0, W, stripH);
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  let ty = pad * 0.6;
  for (const l of rows) {
    ctx.fillStyle = l.color || COLORS.chipText;
    ctx.font = `${l.title ? 800 : 700} ${fh(l) * fit}px system-ui, -apple-system, sans-serif`;
    ctx.fillText(l.text, pad, ty);
    ty += fh(l) * fit * 1.4;
  }
  ctx.drawImage(srcCanvas, 0, stripH);
  return out;
}

/**
 * Multi-unit annotation: several windows/doors sharing one opening
 * (mulled windows, door + transom, sidelites) — each unit gets its own
 * dashed box, numbered badge and width/height labels; optionally an outer
 * blue box with the OVERALL opening measurement.
 *
 * @param {Object} opts
 * @param {string} opts.dataUrl        source photo (dataURL)
 * @param {Array}  opts.units          [{box, widthText, heightText, labelText, expectedText, match}]
 * @param {string} opts.locationText   header line, e.g. `#4816041 · Porta com janela em cima`
 * @param {Object|null} opts.overall   {widthText, heightText} for the whole opening ("" allowed)
 * @returns {Promise<string>} annotated JPEG dataURL
 */
export function annotateMeasurePhotoMulti({ dataUrl, units, locationText, overall, oldLabel }) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const W = img.naturalWidth;
        const H = img.naturalHeight;
        const canvas = document.createElement("canvas");
        canvas.width = W;
        canvas.height = H;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        const s = Math.max(W, H) / 1000;
        const lw = Math.max(2.5, 3.2 * s);
        const font = Math.max(13, 19 * s);
        const overallColor = "#2563eb";
        const badgeColor = "#7c3aed"; // violet — stands out from red (wrong) and blue (overall)

        const labelQueue = [];
        (units || []).forEach((u, i) => {
          const b = u.box && u.box.w > 0 && u.box.h > 0 ? u.box : { x: 0.14, y: 0.1, w: 0.72, h: 0.72 };
          const bx = b.x * W, by = b.y * H, bw = b.w * W, bh = b.h * H;
          const color = u.match === false ? COLORS.bad : u.match === true ? COLORS.good : COLORS.neutral;

          // rotate this unit's marks around its center for angled photos
          const ang = ((b.angle || 0) * Math.PI) / 180;
          ctx.save();
          if (ang) {
            ctx.translate(bx + bw / 2, by + bh / 2);
            ctx.rotate(ang);
            ctx.translate(-(bx + bw / 2), -(by + bh / 2));
          }

          if (u.shape && u.shape !== "rectangle") {
            ctx.save();
            ctx.strokeStyle = "rgba(255,255,255,0.95)"; ctx.lineWidth = lw * 1.5;
            strokeShapeOutline(ctx, u.shape, bx, by, bw, bh, u.side, u.dims);
            ctx.strokeStyle = color; ctx.lineWidth = lw * 0.75;
            strokeShapeOutline(ctx, u.shape, bx, by, bw, bh, u.side, u.dims);
            ctx.restore();
          }

          drawShapeArrows(ctx, shapeArrows({ shape: u.shape, side: u.side, bx, by, bw, bh, dims: u.dims, widthText: u.widthText, heightText: u.heightText, s }),
            color, lw * 0.85, font * 0.92, W, H, labelQueue);

          // numbered badge (e.g. `1 · 105A`) pinned to the top edge of the unit
          // drawn in VIOLET so it never blends with the red/green measurements
          const badge = u.labelText || String(i + 1);
          ctx.font = `800 ${font * 0.88}px system-ui, -apple-system, sans-serif`;
          const bwid = ctx.measureText(badge).width + font;
          labelQueue.push({ text: badge, lx: Math.min(Math.max(bx + bwid / 2, bx + bw / 2), bx + bw - bwid / 2), ly: by + font * 0.9, color: badgeColor, font: font * 0.88, t: ctx.getTransform() });

          ctx.restore(); // end unit rotation
        });

        // OVERALL opening: outer blue dashed box + arrows below/right
        if (overall && (overall.widthText || overall.heightText)) {
          const ub = unionBoxPx((units || []).map((u) => u.box), W, H);
          const pad = 13 * s;
          const ox = Math.max(2, ub.x1 - pad);
          const oy = Math.max(2, ub.y1 - pad);
          const ox2 = Math.min(W - 2, ub.x2 + pad);
          const oy2 = Math.min(H - 2, ub.y2 + pad);
          ctx.save();
          ctx.setLineDash([18 * s, 11 * s]);
          ctx.strokeStyle = "rgba(255,255,255,0.9)";
          ctx.lineWidth = lw * 1.9;
          ctx.strokeRect(ox, oy, ox2 - ox, oy2 - oy);
          ctx.strokeStyle = overallColor;
          ctx.lineWidth = lw;
          ctx.strokeRect(ox, oy, ox2 - ox, oy2 - oy);
          ctx.restore();

          if (overall.widthText) {
            const owy = Math.min(H - 24 * s, oy2 + 26 * s);
            drawArrow(ctx, ox, owy, ox2, owy, overallColor, lw);
            labelQueue.push({ text: overall.widthText, lx: (ox + ox2) / 2, ly: Math.min(H - font, owy + font * 1.35), color: overallColor, font, t: ctx.getTransform() });
          }
          if (overall.heightText) {
            const rightRoom = ox2 < W - 46 * s;
            const ohx = rightRoom ? Math.min(W - 24 * s, ox2 + 26 * s) : Math.max(24 * s, ox - 26 * s);
            drawArrow(ctx, ohx, oy, ohx, oy2, overallColor, lw);
            const lx = rightRoom
              ? Math.min(W - font * 2.4, ohx + font * 1.7)
              : Math.max(font * 2.4, ohx - font * 1.7);
            labelQueue.push({ text: overall.heightText, lx, ly: (oy + oy2) / 2, color: overallColor, font, t: ctx.getTransform() });
          }
        }
        flushLabels(ctx, labelQueue);

        // header strip ABOVE the photo: location + "old wrong measurements"
        // warning + one line per unit with PDF reference and/or written note
        const lines = [];
        if (locationText) lines.push({ text: locationText, title: true });
        const anyWrong = (units || []).some((u) => u.expectedText && u.match === false);
        if (anyWrong && oldLabel) lines.push({ text: `⚠ ${oldLabel}:`, color: "#fca5a5" });
        (units || []).forEach((u, i) => {
          const note = clipNote(u.noteText);
          if (u.expectedText || note) {
            const flag = u.match === false ? "✗" : u.match === true ? "✓" : "";
            const name = `${u.labelText || i + 1}${u.subtypeText ? ` · ${u.subtypeText}` : ""}`;
            const segs = [];
            if (u.expectedText) segs.push(`PDF ${u.expectedText} ${flag}`.trim());
            if (note) segs.push(`📝 ${note}`);
            lines.push({
              text: `${name}: ${segs.join(" — ")}`,
              color: u.expectedText
                ? (u.match === false ? "#fca5a5" : u.match === true ? "#6ee7b7" : "#fcd34d")
                : "#e2e8f0",
            });
          }
        });
        if (overall && overall.widthText && overall.heightText) {
          lines.push({ text: `Opening: ${overall.widthText} x ${overall.heightText}`, color: "#93c5fd" });
        }
        const out = composeWithHeader(canvas, lines, s, font);

        resolve(out.toDataURL("image/jpeg", 0.9));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}
